import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar as CalendarWidget } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useBrand } from '../contexts/BrandContext';
import { formatCurrency } from '../lib/utils';
import { SEO, ListingSchema, BreadcrumbSchema } from '../components/SEO';
import {
    Headphones, Ticket, MapPin, ArrowLeft, Clock, Users, Minus, Plus,
    CalendarDays, ShoppingBag, Shield, ChevronRight, AlertCircle, Check,
    CircleCheck, Info, Zap, Sun, Sunset
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExperiencePage() {
    const { listingId } = useParams();
    const navigate = useNavigate();
    const brand = useBrand();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);

    const [selectedOption, setSelectedOption] = useState(null);
    const [adults, setAdults] = useState(1);
    const [children, setChildren] = useState(0);
    const [infants, setInfants] = useState(0);
    const [visitDate, setVisitDate] = useState('');
    const [visitTime, setVisitTime] = useState('');
    const [availability, setAvailability] = useState(null);
    const [loadingAvail, setLoadingAvail] = useState(false);
    const [serverNow, setServerNow] = useState(null);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const bookingRef = useRef(null);

    useEffect(() => { fetchListing(); }, [listingId]);

    useEffect(() => {
        if (visitDate && listing) fetchAvailability();
        else { setAvailability(null); setVisitTime(''); }
    }, [visitDate]);

    const fetchListing = async () => {
        try {
            const res = await fetch(`${API}/api/public/listings/${listingId}`);
            if (!res.ok) throw new Error();
            const data = await res.json();
            setListing(data);
            if (data.options?.length > 0) setSelectedOption(data.options[0]);
        } catch { navigate('/'); }
        finally { setLoading(false); }
    };

    const fetchAvailability = async () => {
        setLoadingAvail(true);
        try {
            const res = await fetch(`${API}/api/public/listings/${listingId}/availability?date=${visitDate}`);
            const data = await res.json();
            setAvailability(data);
            if (data.server_now) setServerNow(data.server_now);
            setVisitTime('');
        } catch { setAvailability(null); }
        finally { setLoadingAvail(false); }
    };

    if (loading) return <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;
    if (!listing) return null;

    const pricing = selectedOption?.pricing || { adult: 0, child: 0, infant: 0 };
    const total = (pricing.adult || 0) * adults + (pricing.child || 0) * children + (pricing.infant || 0) * infants;
    const lowestPrice = Math.min(...(listing.options || []).map(o => o.pricing?.adult || 999));
    const currency = listing.currency?.toUpperCase() || 'EUR';
    const hasSchedule = Object.keys(listing.schedule || {}).length > 0;
    const needsDateTime = hasSchedule && selectedOption?.has_tickets;

    const isSlotPastCutoff = (slot) => {
        if (!selectedOption || !slot.slot_datetime) return false;
        const cutoffHours = selectedOption.cutoff_hours || 0;
        if (cutoffHours === 0) return false;
        const now = serverNow ? new Date(serverNow) : new Date();
        const slotDt = new Date(slot.slot_datetime);
        const cutoffDt = new Date(slotDt.getTime() - cutoffHours * 60 * 60 * 1000);
        return now >= cutoffDt;
    };

    const formatCutoff = (hours) => {
        if (!hours) return '';
        if (hours < 24) return `${hours}h`;
        const days = Math.round(hours / 24);
        return `${days}d`;
    };

    const canProceed = adults + children + infants > 0 && selectedOption && (!needsDateTime || (visitDate && visitTime));

    const getImageUrl = (img) => img && (img.startsWith('http') ? img : `${API}${img}`);
    const minDate = listing.available_from || new Date().toISOString().split('T')[0];
    const maxDate = listing.available_until || '';

    const handleCheckout = () => {
        const params = new URLSearchParams({
            listing: listingId, option: selectedOption.id,
            adults: adults.toString(), children: children.toString(), infants: infants.toString(),
            date: visitDate, time: visitTime,
        });
        navigate(`/checkout?${params}`);
    };

    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
    const getImgUrl = (img) => { if (!img) return ''; return img.startsWith('http') ? img : `${API}${img}`; };

    return (
        <div className="min-h-screen bg-[#0A0D14]">
            <SEO
                title={`${listing.title}${listing.city_name ? ` — ${listing.city_name}` : ''} | Book Now`}
                description={listing.short_description || listing.description || `Book ${listing.title} with audio guide and skip-the-line tickets. Instant confirmation available.`}
                image={getImgUrl(listing.featured_image)}
                url={pageUrl}
                type="product"
            />
            <ListingSchema listing={listing} url={pageUrl} />
            <BreadcrumbSchema items={[
                { name: 'Home', url: `${window.location.origin}/` },
                ...(listing.city_name ? [{ name: listing.city_name, url: `${window.location.origin}/city/${listing.city_name?.toLowerCase().replace(/\s+/g, '-')}` }] : []),
                { name: listing.title }
            ]} />
            <header className="fixed top-0 left-0 right-0 z-50 glass">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 sm:gap-3">
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.platform_name} className="h-7 sm:h-9 max-w-[120px] sm:max-w-[160px] object-contain" />
                        ) : (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-gold flex items-center justify-center">
                                <Headphones className="w-4 h-4 sm:w-5 sm:h-5 text-[#0A0D14]" />
                            </div>
                        )}
                        {!brand.logo_url && <span className="font-outfit font-semibold text-base sm:text-xl text-white">{brand.platform_name}</span>}
                    </Link>
                    <Link to="/access"><Button size="sm" className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-1.5 text-xs h-9 px-3"><Ticket className="w-3.5 h-3.5" /> I Have a Code</Button></Link>
                </div>
            </header>

            <div className="pt-16 pb-24 lg:pb-24 pb-32">
                {/* Hero */}
                <div className="relative h-[280px] sm:h-[360px] lg:h-[420px] overflow-hidden">
                    {getImageUrl(listing.featured_image) ? <img src={getImageUrl(listing.featured_image)} alt={listing.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-gold/10 to-gold/5" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 max-w-7xl mx-auto">
                        <div className="flex items-center gap-2 mb-3">
                            <MapPin className="w-4 h-4 text-gold" /><span className="text-gold text-sm">{listing.city_name} {listing.city_country && `· ${listing.city_country}`}</span>
                            <span className="text-white/40 text-sm ml-2">{listing.category}</span>
                            {listing.instant_confirmation && <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[11px] font-medium border border-emerald-500/20 flex items-center gap-1" data-testid="instant-confirmation-badge"><Zap className="w-3 h-3" />Instant Confirmation</span>}
                        </div>
                        <h1 className="font-outfit text-2xl sm:text-4xl lg:text-5xl font-bold text-white" data-testid="experience-title">{listing.title}</h1>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Left */}
                        <div className="lg:col-span-2 space-y-8">
                            <div>
                                <h2 className="font-outfit text-lg font-semibold text-white mb-3">About this experience</h2>
                                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed whitespace-pre-line">{listing.description}</p>
                            </div>

                            {/* Options Cards */}
                            <div>
                                <h2 className="font-outfit text-lg font-semibold text-white mb-4">Choose Your Package</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {(listing.options || []).map((opt, idx) => {
                                        const isCombo = opt.has_tickets || (opt.pricing?.adult > 0 && idx > 0);
                                        const accentBg = isCombo ? 'bg-amber-500/10' : 'bg-emerald-500/10';
                                        const accentText = isCombo ? 'text-amber-400' : 'text-emerald-400';
                                        return (
                                            <button key={opt.id} onClick={() => {
                                                setSelectedOption(opt);
                                                setVisitTime('');
                                                const allowed = opt.allowed_travelers || ['adults', 'children', 'infants'];
                                                if (!allowed.includes('children')) setChildren(0);
                                                if (!allowed.includes('infants')) setInfants(0);
                                            }}
                                                className={`text-left p-4 sm:p-5 rounded-xl border transition-all ${selectedOption?.id === opt.id ? 'border-gold bg-gold/5 ring-1 ring-gold/30' : 'border-white/10 bg-[#121620] hover:border-white/20'}`}
                                                data-testid={`option-${opt.id}`}>
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accentBg}`}>
                                                        {isCombo ? <Ticket className={`w-4 h-4 ${accentText}`} /> : <Headphones className={`w-4 h-4 ${accentText}`} />}
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3 className="font-medium text-white text-sm">{opt.name}</h3>
                                                        {selectedOption?.id === opt.id && <Check className="w-4 h-4 text-gold inline ml-1" />}
                                                    </div>
                                                </div>
                                                {opt.description && <p className="text-xs text-muted-foreground mb-3">{opt.description}</p>}
                                                <div className="flex items-center justify-between">
                                                    <p className="text-sm text-white">From <span className={`text-lg font-bold ${accentText}`}>{formatCurrency(opt.pricing?.adult || 0, currency)}</span> / adult</p>
                                                    {opt.cutoff_hours > 0 && <span className="text-[10px] text-muted-foreground bg-white/5 px-2 py-0.5 rounded-full">{formatCutoff(opt.cutoff_hours)} cut-off</span>}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Inclusions */}
                            {(listing.inclusions || []).length > 0 && (
                                <div data-testid="inclusions-section">
                                    <h2 className="font-outfit text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                        <CircleCheck className="w-5 h-5 text-emerald-400" /> What's Included
                                    </h2>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                                        {listing.inclusions.map((item, i) => (
                                            <div key={`incl-${item.slice(0, 20)}-${i}`} className="flex items-start gap-2.5">
                                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 shrink-0" />
                                                <span className="text-sm text-muted-foreground">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Know Before You Go */}
                            {(listing.know_before_you_go || []).length > 0 && (
                                <div data-testid="know-before-section">
                                    <h2 className="font-outfit text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                        <Info className="w-5 h-5 text-amber-400" /> Know Before You Go
                                    </h2>
                                    <div className="space-y-2.5">
                                        {listing.know_before_you_go.map((item, i) => (
                                            <div key={`kbyg-${item.slice(0, 20)}-${i}`} className="flex items-start gap-2.5">
                                                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                                                <span className="text-sm text-muted-foreground">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Age Info */}
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-blue-400" /> Adult: {listing.adult_age_min}+ yrs</span>
                                <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-green-400" /> Child: {listing.child_age_min}-{listing.child_age_max} yrs</span>
                                <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-purple-400" /> Infant: 0-{listing.infant_age_max} yrs</span>
                            </div>

                            {/* Schedule Info */}
                            {Object.keys(listing.schedule || {}).length > 0 && (
                                <div>
                                    <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-gold" /> Schedule</h3>
                                    <div className="flex flex-wrap gap-3">
                                        {Object.entries(listing.schedule).map(([day, slots]) => (
                                            <div key={day} className="p-3 rounded-lg bg-[#121620] border border-white/10">
                                                <p className="text-xs text-gold font-medium capitalize mb-1">{day}</p>
                                                {slots.map((s, i) => <p key={`${day}-${s.time}`} className="text-xs text-muted-foreground">{s.time}</p>)}
                                            </div>
                                        ))}
                                    </div>
                                    {listing.available_from && <p className="text-xs text-muted-foreground mt-3">Available: {listing.available_from} — {listing.available_until || 'ongoing'}</p>}
                                </div>
                            )}
                        </div>

                        {/* Right: Booking Card */}
                        <div className="lg:col-span-1" ref={bookingRef}>
                            <Card className="bg-[#121620] border-white/10 sticky top-20" data-testid="booking-card">
                                <CardContent className="p-6 space-y-5">
                                    <h3 className="font-outfit text-lg font-semibold text-white">Book Now</h3>

                                    {/* Selected Option */}
                                    {selectedOption && (() => {
                                        const optIdx = (listing.options || []).findIndex(o => o.id === selectedOption.id);
                                        const isCombo = selectedOption.has_tickets || (selectedOption.pricing?.adult > 0 && optIdx > 0);
                                        return (
                                            <div className={`p-3 rounded-lg border ${isCombo ? 'border-amber-400/30 bg-amber-500/5' : 'border-emerald-400/30 bg-emerald-500/5'}`}>
                                                <p className="text-sm font-medium text-white">{selectedOption.name}</p>
                                                <p className="text-xs text-muted-foreground">{formatCurrency(selectedOption.pricing?.adult || 0, currency)}/{selectedOption.has_tickets ? 'adult' : 'person'}</p>
                                            </div>
                                        );
                                    })()}

                                    {/* Date & Time */}
                                    {needsDateTime && (
                                        <>
                                            <div>
                                                <Label className="text-white/70 text-xs uppercase tracking-wider mb-2 block">Visit Date</Label>
                                                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                                                    <PopoverTrigger asChild>
                                                        <button onClick={() => setCalendarOpen(true)} className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 hover:border-gold/30 text-left transition-colors" data-testid="visit-date-input">
                                                            <span className={visitDate ? 'text-white text-sm' : 'text-white/30 text-sm'}>
                                                                {visitDate ? new Date(visitDate + 'T00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'Select a date'}
                                                            </span>
                                                            <CalendarDays className="w-4 h-4 text-gold" />
                                                        </button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-auto p-0 bg-[#1a1f2e] border-white/10" align="start">
                                                        <CalendarWidget
                                                            mode="single"
                                                            selected={visitDate ? new Date(visitDate + 'T00:00') : undefined}
                                                            onSelect={(date) => {
                                                                if (date) {
                                                                    const y = date.getFullYear();
                                                                    const m = String(date.getMonth() + 1).padStart(2, '0');
                                                                    const d = String(date.getDate()).padStart(2, '0');
                                                                    setVisitDate(`${y}-${m}-${d}`);
                                                                } else {
                                                                    setVisitDate('');
                                                                }
                                                                setCalendarOpen(false);
                                                            }}
                                                            disabled={(date) => {
                                                                const today = new Date();
                                                                today.setHours(0, 0, 0, 0);
                                                                return date < today;
                                                            }}
                                                            className="rounded-lg"
                                                        />
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                            {visitDate && loadingAvail && <div className="flex justify-center py-3"><div className="w-5 h-5 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>}
                                            {visitDate && availability && !loadingAvail && (
                                                availability.available ? (
                                                    <div>
                                                        <Label className="text-white/70 text-xs uppercase tracking-wider mb-2 block">Time Slot</Label>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {availability.slots.map(slot => {
                                                                const pastCutoff = isSlotPastCutoff(slot);
                                                                const disabled = slot.remaining <= 0 || pastCutoff;
                                                                return (
                                                                    <button key={slot.time} onClick={() => setVisitTime(slot.time)}
                                                                        disabled={disabled}
                                                                        className={`p-2.5 rounded-lg border text-center transition-all ${visitTime === slot.time ? 'border-gold bg-gold/5' : !disabled ? 'border-white/10 hover:border-white/20' : 'border-white/5 opacity-40 cursor-not-allowed'}`}
                                                                        data-testid={`time-slot-${slot.time}`}>
                                                                        <div className="flex items-center justify-center gap-1.5">
                                                                            {parseInt(slot.time.split(':')[0]) < 12
                                                                                ? <Sun className={`w-3.5 h-3.5 ${visitTime === slot.time ? 'text-amber-400' : 'text-amber-400/50'}`} />
                                                                                : <Sunset className={`w-3.5 h-3.5 ${visitTime === slot.time ? 'text-orange-400' : 'text-orange-400/50'}`} />
                                                                            }
                                                                            <span className="text-sm font-medium text-white">{slot.time}</span>
                                                                        </div>
                                                                        <p className="text-xs text-muted-foreground mt-0.5">
                                                                            {pastCutoff ? 'Sale closed' : slot.remaining > 0 ? `${slot.remaining} left` : 'Sold out'}
                                                                        </p>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                        {selectedOption?.cutoff_hours > 0 && (
                                                            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                                                <Clock className="w-3 h-3" /> Sales close {formatCutoff(selectedOption.cutoff_hours)} before each slot
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                                                        <AlertCircle className="w-4 h-4 text-red-400" />
                                                        <p className="text-xs text-red-400">Not available on this date. Please select another day.</p>
                                                    </div>
                                                )
                                            )}
                                        </>
                                    )}

                                    {/* Travelers */}
                                    <div>
                                        <Label className="text-white/70 text-xs uppercase tracking-wider mb-3 block">Travelers</Label>
                                        <div className="space-y-3">
                                            {selectedOption && !selectedOption.has_tickets ? (
                                                /* Audio Guide Only — simple traveler count, no age categories */
                                                <div className="flex items-center justify-between">
                                                    <div><p className="text-sm text-white">Traveler</p><p className="text-xs text-muted-foreground">{formatCurrency(pricing.adult || 0, currency)} per person</p></div>
                                                    <div className="flex items-center gap-3">
                                                        <button onClick={() => setAdults(Math.max(1, adults - 1))} className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-white hover:border-white/30"><Minus className="w-4 h-4" /></button>
                                                        <span className="text-white font-medium w-6 text-center">{adults}</span>
                                                        <button onClick={() => setAdults(adults + 1)} className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-white hover:border-white/30"><Plus className="w-4 h-4" /></button>
                                                    </div>
                                                </div>
                                            ) : (
                                                /* Ticket options — show age-based categories */
                                                (() => {
                                                    const allowed = selectedOption?.allowed_travelers || ['adults', 'children', 'infants'];
                                                    const types = [
                                                        { key: 'adults', label: 'Adult', ageInfo: `${listing.adult_age_min || 18}+ yrs`, price: pricing.adult, val: adults, set: setAdults },
                                                        { key: 'children', label: 'Child', ageInfo: `${listing.child_age_min || 3}-${listing.child_age_max || 17} yrs`, price: pricing.child, val: children, set: setChildren },
                                                        { key: 'infants', label: 'Infant', ageInfo: `0-${listing.infant_age_max || 2} yrs`, price: pricing.infant, val: infants, set: setInfants },
                                                    ];
                                                    return types.filter(t => allowed.includes(t.key)).map(t => (
                                                        <div key={t.label} className="flex items-center justify-between">
                                                            <div><p className="text-sm text-white">{t.label}</p><p className="text-xs text-muted-foreground">{t.ageInfo} · {formatCurrency(t.price || 0, currency)}</p></div>
                                                            <div className="flex items-center gap-3">
                                                                <button onClick={() => t.set(Math.max(t.key === 'adults' ? 1 : 0, t.val - 1))} className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-white hover:border-white/30"><Minus className="w-4 h-4" /></button>
                                                                <span className="text-white font-medium w-6 text-center">{t.val}</span>
                                                                <button onClick={() => t.set(t.val + 1)} className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-white hover:border-white/30"><Plus className="w-4 h-4" /></button>
                                                            </div>
                                                        </div>
                                                    ));
                                                })()
                                            )}
                                        </div>
                                    </div>

                                    {/* Total */}
                                    <div className="pt-4 border-t border-white/10">
                                        <div className="flex items-center justify-between mb-4">
                                            <span className="text-muted-foreground text-sm">Total</span>
                                            <span className="text-2xl font-outfit font-bold text-white" data-testid="total-price">{formatCurrency(total, currency)}</span>
                                        </div>
                                        {needsDateTime && visitDate && !visitTime && (
                                            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-3 animate-pulse">
                                                <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                                                <p className="text-xs text-amber-400">Please select a time slot above to continue</p>
                                            </div>
                                        )}
                                        <Button onClick={handleCheckout} disabled={!canProceed || total === 0}
                                            className="w-full bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2 h-12 text-base font-semibold disabled:opacity-40" data-testid="proceed-checkout-btn">
                                            <ShoppingBag className="w-5 h-5" /> {needsDateTime && !visitTime ? 'Select Time to Continue' : 'Proceed to Checkout'}
                                        </Button>
                                        <p className="text-xs text-muted-foreground text-center mt-3 flex items-center justify-center gap-1"><Shield className="w-3 h-3" /> Secure payment via Stripe</p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Mobile CTA */}
            <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden glass border-t border-white/10" data-testid="mobile-sticky-cta">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-xs text-white/40 truncate">{selectedOption?.name || 'Select a package'}</p>
                        <p className="text-lg font-outfit font-bold text-white">
                            {total > 0 ? formatCurrency(total, currency) : `From ${formatCurrency(lowestPrice, currency)}`}
                        </p>
                    </div>
                    <Button
                        onClick={() => {
                            if (canProceed && total > 0) { handleCheckout(); }
                            else { bookingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
                        }}
                        className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2 h-11 px-6 text-sm font-semibold shrink-0"
                        data-testid="mobile-book-btn"
                    >
                        <ShoppingBag className="w-4 h-4" />
                        {needsDateTime && visitDate && !visitTime ? 'Select Time' : canProceed && total > 0 ? 'Checkout' : 'Book Now'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
